import AlertTriangle from '@streamlinehq/streamlinehq/img/streamline-regular/interface-essential/alerts/alert-triangle.svg';
import { StateMessage } from '@upright/ui/common/generic/components/state-message';
import { isValidElement, ReactNode } from 'react';
import styled from 'styled-components';
import { BouSvgIcon } from '../../../../../components/bou-svg-icon';
import { BouColor } from '../../../../../global/bou-colors';
import { BouSpacingLevel, fluidPadding, fluidSpacing } from '../../../../../layout/bou-spacing';
import { TableV2Variant } from '../../types';

/**
 * The row a table shows when it has no rows.
 *
 * The `plain` variant, the table look for new work, takes the design system's empty state: one
 * left-aligned `gray-600` sentence on the table's own white ground, inset like a first cell, with any
 * next action the caller passes after it (design/50-components.md "Empty and loading states"). A
 * shaded well would be a second surface inside the frame, and surfaces are separated by a border,
 * not by shading. The `default` variant keeps its centred gray well for a plain sentence.
 *
 * A `StateMessage` takes the plain ground in either variant, error rows included. It is the design
 * system's section state, with its own heading, copy colour and actions, and it is specified
 * left-aligned on the white canvas under its section's heading (design/50-components.md "Empty and
 * loading states"). Inside the well it would be centred on a tinted panel, and the error row would
 * add a warning glyph beside a heading that already says what went wrong.
 */
export function EmptyState(props: { content?: string | JSX.Element; variant?: TableV2Variant }) {
  const content = props.content || 'Table is empty';
  return (
    <TableWithOneFullWidthRow>
      {props.variant === 'plain' || isStateMessage(content) ? <PlainRoot>{content}</PlainRoot> : <Root>{content}</Root>}
    </TableWithOneFullWidthRow>
  );
}

export function ErrorState(props: { content?: string | JSX.Element }) {
  const content = props.content || 'Something went wrong';
  return (
    <TableWithOneFullWidthRow>
      {isStateMessage(content) ? (
        <PlainRoot>{content}</PlainRoot>
      ) : (
        <Root>
          <IconWrapper icon={<AlertTriangle />} />
          {content}
        </Root>
      )}
    </TableWithOneFullWidthRow>
  );
}

function isStateMessage(content: string | JSX.Element) {
  return isValidElement(content) && content.type === StateMessage;
}

const TableWithOneFullWidthRow = (props: { children: ReactNode }) => (
  <tbody style={{ display: 'contents' }}>
    <tr style={{ display: 'contents' }}>
      <td style={{ gridColumnStart: 1, gridColumnEnd: -1 }}>{props.children}</td>
    </tr>
  </tbody>
);

const Root = styled.div`
  ${fluidPadding(BouSpacingLevel.SMALL)}
  color: ${BouColor.TOOL_TEXT_DARK_GRAY};
  background-color: ${BouColor.TOOL_TABLE_BACKGROUND_LIGHT_GRAY};
  min-height: 10em;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const PlainRoot = styled.div`
  padding: var(--space__lg);
  color: var(--color__gray-600);
  font-size: var(--font-size__md);
  line-height: var(--line-height__sm);
  text-align: left;
`;

const IconWrapper = styled(BouSvgIcon)`
  ${fluidSpacing('padding-right', BouSpacingLevel.MICRO)}
`;
